// src/lib/domain-constants.js — constantes domaine FBZ.
// Regroupe les enums et configs par défaut utilisés dans plusieurs composants.
// Importe DS pour les couleurs sémantiques (cohérent avec convention couleur CLAUDE.md).

import { DS } from './ui'

// ── Plateformes ───────────────────────────────────────────────────────────
// Couleurs brand : Airbnb rouge officiel, Booking bleu officiel, Direct vert.
// 'manuel' (Lot 4 V2-create-manual) : résa saisie à la main sur un logement V2
// (chip verrouillé dans ResaModal). Brun sobre, distinct des 3 canaux OTA.
export const PL_LABEL = { airbnb: "Airbnb", booking: "Booking.com", direct: "Direct", manuel: "Manuel" }
export const PL_COLOR = { airbnb: "#FF5A5F", booking: "#003580", direct: "#2d7a3a", manuel: "#8a6d3b" }

// ── Incidents ─────────────────────────────────────────────────────────────
export const INCIDENT_TYPES = [
  "Petite serviette", "Grande serviette",
  "Drap lit simple", "Drap lit double",
  "Taie d’oreiller",
  "Petite housse de couette", "Grande housse de couette",
  "Dégât matériel", "Autre",
]

export const INCIDENT_STATUS_MENAGE = ["À traiter", "Récupérable", "Non récupérable", "À racheter", "Remplacé"]
export const INCIDENT_STATUS = ["En cours", "Résolu", "Remboursé"]
export const STATUS_COLOR = { "En cours": "#d97706", "Résolu": "#2d7a3a", "Remboursé": "#3d5166" }
export const MENAGE_STATUS_COLOR = {
  "À traiter": "#d97706",
  "À vérifier": "#d97706", // ADR-228 — ambre explicite (incident pas encore jugé)
  "Récupérable": "#2d7a3a",
  "Non récupérable": "#c0392b",
  "À racheter": "#d97706",
  "Remplacé": "#3d5166",
}

// ── Incidents — 5 états financiers (ADR-231-fin, chantier ⑥) ────────────────
// Axe FINANCIER d'une dégradation, ORTHOGONAL à l'axe QUALITÉ (statutMenage,
// machine ADR-227/228). REMPLACE l'ancien champ `traitement` (fbz/info).
// Défaut d'une nouvelle dégradation = null (« À qualifier », aucun des 5 états)
// → l'admin qualifie (badge « incidents non qualifiés » côté admin).
//
// Décisions produit (Dimitri) :
//   - AirCover est perçu par le PROPRIO → la facture reste au BRUT, JAMAIS de
//     déduction AirCover (sinon double remboursement). AirCover HORS facture.
//   - Compta (lecture à la volée) : seul « Achat FULLBNBZEN » = charge FBZ.
//     « Refacturé propriétaire » = débours neutre (déjà porté par la facture).
//     « Remboursé AirCover » = flux proprio hors compta FBZ.
export const ETAT_FINANCIER = {
  SANS_COUT: "Sans coût",
  REFACTURE_PROPRIO: "Refacturé propriétaire",
  ACHAT_FBZ: "Achat FULLBNBZEN",
  REMBOURSE_AIRCOVER: "Remboursé AirCover",
  AIRCOVER_REFACTURE: "AirCover + refacturé",
}

// Ordre d'affichage du sélecteur (cas général : vrais proprios).
export const ETATS_FINANCIERS = [
  ETAT_FINANCIER.SANS_COUT,
  ETAT_FINANCIER.REFACTURE_PROPRIO,
  ETAT_FINANCIER.ACHAT_FBZ,
  ETAT_FINANCIER.REMBOURSE_AIRCOVER,
  ETAT_FINANCIER.AIRCOVER_REFACTURE,
]

// Sous-location (lc1/lc2/lc3, p6 « Maison Zen ») : FBZ est l'hôte Airbnb, il
// encaisse l'AirCover et il n'y a PAS de facture proprio → sélecteur RESTREINT
// à « Sans coût » et « Achat FULLBNBZEN ». L'AirCover perçu par FBZ est saisi à
// part (champ `aircoverFbz`) en atténuation de charge compta.
export const ETATS_FINANCIERS_SOUSLOC = [
  ETAT_FINANCIER.SANS_COUT,
  ETAT_FINANCIER.ACHAT_FBZ,
]

// ── Compta — charges fixes par défaut ─────────────────────────────────────
// Initialisation du blob fbz8-compta.chargesFixes (modifiable via UI Compta).
export const CHARGES_FIXES_DEFAULT = [
  { id: "cf1", label: "Comptable", montant: 87.60 },
  { id: "cf2", label: "Free (internet)", montant: 5.00 },
  { id: "cf3", label: "Channel Manager", montant: 117.40 },
  { id: "cf4", label: "Pricelabs", montant: 35.00 },
  { id: "cf5", label: "Sunver", montant: 43.20 },
]

// ── Compta — moyens de paiement (dépenses variables) ─────────────────────
export const PAYE_PAR_OPTIONS = [
  { value: "shine", label: "💳 Shine", color: DS.navy },
  { value: "neisia", label: "👤 Néïsia perso", color: "#7b5ea7" },
  { value: "dimitri", label: "👤 Dimitri perso", color: "#2d7a5e" },
  { value: "cash", label: "💵 Cash", color: DS.amber },
]

// ── Prospection ──────────────────────────────────────────────────────────
// CHANTIER 61 : statut "pas_contacte" = défaut du scraper (lead jamais approché).
// LOT 4 OPÉRATEUR B : la donnée sous-jacente ne bouge PAS — la vue Logements ›
// « À démarcher » (ADemarcherCtrl) mappe ses libellés PAR-DESSUS ces statuts
// (pas_contacte→À regarder · discussion+proposition→En négociation · etc.).
export const PROSPECT_STATUTS = [
  { id: "pas_contacte", label: "Pas contacté", color: "#87b8e6", bg: "#eef6ff" },
  { id: "contacte", label: "Contacté", color: "#87b8e6", bg: "#eef6ff" },
  { id: "discussion", label: "En discussion", color: DS.amber, bg: "#fff8e6" },
  { id: "proposition", label: "Proposition", color: "#7b5ea7", bg: "#f5f0fa" },
  { id: "signe", label: "Signé ✅", color: DS.green, bg: "#edf7f0" },
  { id: "perdu", label: "Perdu", color: DS.red, bg: "#fdf2f2" },
]

export const INTERACTION_TYPES = ["📞 Appel", "📧 Email", "🤝 RDV", "💬 Message", "📋 Relance"]
export const FORMULES = ["Smart 15%", "Confort 22%", "ZEN+ 32%", "À négocier"]

// Décodage des codes courts Mélo en noms lisibles (UI uniquement, data stockée brute).
export const SOURCE_LABEL_MAP = { LBC: "LeBonCoin", SL: "SeLoger", DB: "monbien.fr", "L-Immo": "Logic-Immo" }

// ── Config initiale par défaut ───────────────────────────────────────────
// Premier load (avant que le user n'ait sauvé une config persistée). Inclut
// la liste consommables seed et la structure props/logs/chambres réelle FBZ.
export const INIT_CFG = {
  // LOT « champs orphelins » — `derNum` RETIRÉ de la forme par défaut. C'était
  // un vestige du compteur de factures d'avant la numérotation en base
  // (invoice_sequences) : AUCUN lecteur dans tout le repo, exactement comme
  // `lastNum` (dont le dernier fallback vivant a déjà été retiré,
  // cf. src/lib/facture-html.js:70). On cesse de le CRÉER ; les valeurs déjà
  // présentes en base ne sont pas touchées (aucune migration, aucune purge).
  co: { nom: "FULLBNBZEN", siret: "927 602 805 00016", adresse: "11 Allée Jean Pierre Vernant, 31000 Toulouse", email: "", tel: "", iban: "", bic: "", tva: false, tauxTva: 20, echeance: "à régler avant le 15 du mois suivant" },
  lastNum: 18, factYear: 2026,
  // ⚠️ CATALOGUE RETIRÉ DU BUNDLE (Dimitri 2026-08-23). Il vivait ici EN DUR,
  // avec les PRIX D'ACHAT — donc téléchargé par le navigateur de tout le monde,
  // agent compris, quel que soit le masquage à l'écran. Et comme il ne bougeait
  // qu'au déploiement, tout produit ajouté en base s'affichait « produit retiré
  // du catalogue » côté terrain.
  // La source est maintenant le SERVEUR : `/api/admin-users?action=catalogue_
  // consommables` (nom, format, unité — zéro montant) pour agent et
  // orchestrateur ; l'admin reçoit le catalogue complet avec `cfg`.
  consommables: [],
  // ── Modèle proprio (cf. ADR-050) ──────────────────────────────────
  // Schéma cible (post-migration via scripts/migrate-proprios-split-nom.js) :
  //   {
  //     id, nom_famille, prenom,        ← source de vérité (saisis par admin)
  //     email, telephone,
  //     adresse, code_postal, ville,
  //     nom,                             ← CHAMP DÉRIVÉ recalculé par
  //                                       propDisplayName(prop) au save
  //                                       (compat ascendante avec ~30 call-sites
  //                                       qui lisent prop.nom directement)
  //     logs: [...]
  //   }
  // Les 6 entrées ci-dessous sont l'ancien format (juste `nom` + `email`).
  // La fonction propDisplayName(prop) gère le fallback : si nom_famille/
  // prenom absents → utilise prop.nom tel quel. Donc safe à laisser tel
  // quel jusqu'à exécution du script de migration.
  // p6 (FULLBNBZEN sous-location) reste hors scope du script (cas spécial).
  props: [
    { id: "p1", nom: "AVELAR Bruno", email: "", logs: [{ id: "l1", nom: "L\'Écrin Toulousain Hypercentre", taux: 20, ical: "https://app.superhote.com/export-ics/HwTDWcFpOV" }] },
    { id: "p2", nom: "ABDELKANI Khadraoui", email: "", logs: [{ id: "l2", nom: "Le Havre de Bruguières", taux: 20, ical: "https://app.superhote.com/export-ics/EspNQYJhb1" },{ id: "l3", nom: "Le Charme de Bruguières", taux: 20, ical: "https://app.superhote.com/export-ics/Z7M8DSuFAD" }] },
    { id: "p3", nom: "MEZAIR Malika", email: "", logs: [{ id: "l4", nom: "L\'Instant Saint-Michel", taux: 20, ical: "https://app.superhote.com/export-ics/WaaLlpQVvO" }] },
    { id: "p4", nom: "LANDILLON Sophie", email: "", logs: [{ id: "l5", nom: "Cocon Toulousain", taux: 20, ical: "https://app.superhote.com/export-ics/oyY6BXHVZ0" },{ id: "l6", nom: "Cachet Toulousain", taux: 20, ical: "https://app.superhote.com/export-ics/xLo78upGtB" }] },
    { id: "p5", nom: "BODEYO Clovis", email: "", logs: [{ id: "l7", nom: "La Douce Escapade Toulousaine", taux: 20, ical: "https://app.superhote.com/export-ics/yL43czKTD5", commissionMode: "degressive", paliers: [{ maxNuits: 6, taux: 22 }, { maxNuits: 13, taux: 20 }], tauxBase: 18 }] },
    { id: "p6", nom: "FULLBNBZEN (sous-location)", email: "", logs: [
      { id: "l8", nom: "Maison Zen", taux: 0,
        chambres: [
          { id: "lc1", nom: "Cocon Naturel", ical: "https://www.airbnb.fr/calendar/ical/1644410039789374794.ics?t=604355bd17f44ac3902b8ce6f0268626" },
          { id: "lc2", nom: "Soleil du Sud", ical: "https://www.airbnb.fr/calendar/ical/1644403440627824696.ics?t=7784491ad7404278b4e9e0fac526daca" },
          { id: "lc3", nom: "Sur un Nuage", ical: "https://www.airbnb.fr/calendar/ical/1644412403165304856.ics?t=87d6a1c39d3544c4bde9c80392a9f1c4" },
        ],
      },
    ] },
  ],
}
